import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AgeGate } from './components/age/AgeGate';
import { Layout } from './components/Layout';
import { ScrollToTop } from './components/ScrollToTop';
import { OnboardingGuard } from './components/OnboardingGuard';
import { AuthGuard } from './components/AuthGuard';
import { Home } from './pages/Home';
import { SearchPage } from './pages/SearchPage';
import { WineryDetail } from './pages/WineryDetail';
import { WineryEdit } from './pages/WineryEdit';
import { WineryWines } from './pages/WineryDetail/WineryWines';
import { VenueManagement } from './pages/VenueManagement';
import { VenueCreate } from './pages/VenueCreate';
import { Wines } from './pages/Wines';
import { WineDetail } from './pages/WineDetail';
import { WineCreate } from './pages/WineCreate';
import { WineEdit } from './pages/WineEdit';
import { Events } from './pages/Events';
import { Experiences } from './pages/Experiences';
import { Itineraries } from './pages/Itineraries';
import { ItineraryBrowse } from './pages/ItineraryBrowse';
import { ItineraryNewPage } from './pages/ItineraryNewPage';
import { ItineraryViewPage } from './pages/ItineraryViewPage';
import { ItineraryEditPage } from './pages/ItineraryEditPage';
import { EventDetail } from './pages/EventDetail';
import { EventCreate } from './pages/EventCreate';
import { EventEdit } from './pages/EventEdit';
import { TastingExperienceCreate } from './pages/TastingExperienceCreate';
import { TastingExperienceEdit } from './pages/TastingExperienceEdit';
import { TastingExperienceDetail } from './pages/TastingExperienceDetail';
import { ApiTest } from './pages/ApiTest';
import { CloudinaryTest } from './pages/CloudinaryTest';
import { MediaLibraryTest } from './pages/MediaLibraryTest';
import { SharedMediaTest } from './pages/SharedMediaTest';
import { AdminMediaManagement } from './pages/AdminMediaManagement';
import { AdminMediaTest } from './pages/AdminMediaTest';
import { HeroCarouselDemo } from './pages/HeroCarouselDemo';
import { TypographyDemo } from './pages/TypographyDemo';
import { ButtonTest } from './pages/ButtonTest';
import { DesignSystemTest } from './pages/DesignSystemTest';
import { UserProfile } from './pages/UserProfile';
import { PublicProfile } from './pages/PublicProfile';
import { Login } from './pages/Login';
import { VerifyEmail } from './pages/VerifyEmail';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Onboarding } from './pages/Onboarding';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Listings } from './pages/Listings';
import { WineTastingFAQ } from './pages/WineTastingFAQ';
import { Help } from './pages/Help';
import { ReportErrors } from './pages/ReportErrors';
import { CheckInLeaderboard } from './pages/CheckInLeaderboard';
import { LatestReviews } from './pages/LatestReviews';
import { WineryXplorerBlog } from './pages/WineryXplorerBlog';
import { Partners } from './pages/Partners';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { AdminLayout } from './components/AdminLayout';
import { AdminDashboard, AdminContent, AdminUsers, AdminUserDetail, AdminSettings, AdminModeration, AdminFeedback, AdminFeedbackDetail } from './pages/admin';
import { OAuthCallback } from './pages/OAuthCallback';

const App = () => {
  return (
    <AuthProvider>
      {/* Alcohol content is age-gated before it is displayed, to everyone —
          anonymous visitors included. Inside AuthProvider so an onboarded user
          who already passed the server-side check is never asked again; above
          Layout so a refusal leaves no navigation to browse on from. */}
      <AgeGate>
        <ScrollToTop />
        <Layout>
          <Routes>
            {/* Public and auth routes - NOT wrapped by OnboardingGuard */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-email/callback" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          
            {/* Public user profile - accessible without auth */}
            <Route path="/users/:username" element={<PublicProfile />} />

            {/* Public itinerary surface (U8) - NOT wrapped by OnboardingGuard so
                anonymous users can browse + view shared itineraries. The auth-
                gated /itineraries, /itineraries/new, /itineraries/:slug and
                /itineraries/:slug/edit routes (U5/U7) are UNTOUCHED below — this
                uses a distinct /p/ namespace so there is no route collision and
                "My Itineraries" / the editor stay auth-gated. getBySlug is a
                publicProcedure (U4) and the view renders a stub, not a 404, for
                non-viewable targets. */}
            <Route path="/itineraries/discover" element={<ItineraryBrowse />} />
            <Route path="/itineraries/p/:slug" element={<ItineraryViewPage />} />

            {/* Footer stub pages - public, no auth required */}
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/wine-tasting-faq" element={<WineTastingFAQ />} />
            <Route path="/help" element={<Help />} />
            <Route path="/report-errors" element={<ReportErrors />} />
            <Route path="/check-in-leaderboard" element={<CheckInLeaderboard />} />
            <Route path="/latest-reviews" element={<LatestReviews />} />
            <Route path="/wineryxplorer-blog" element={<WineryXplorerBlog />} />
            <Route path="/partners" element={<Partners />} />

            {/* Onboarding route - requires authentication */}
            <Route path="/onboarding" element={<AuthGuard><Onboarding /></AuthGuard>} />
          
            {/* Protected routes - wrapped by OnboardingGuard */}
            <Route path="/profile" element={<OnboardingGuard><UserProfile /></OnboardingGuard>} />
            <Route path="/wineries" element={<OnboardingGuard><SearchPage /></OnboardingGuard>} />
            <Route path="/wineries/:id" element={<OnboardingGuard><WineryDetail /></OnboardingGuard>} />
            <Route path="/wineries/:id/wines" element={<OnboardingGuard><WineryWines /></OnboardingGuard>} />
            <Route path="/wineries/:id/edit" element={<OnboardingGuard><WineryEdit /></OnboardingGuard>} />
            <Route path="/wineries/:id/manage" element={<OnboardingGuard><VenueManagement /></OnboardingGuard>} />
            <Route path="/wines" element={<OnboardingGuard><Wines /></OnboardingGuard>} />
            <Route path="/wines/new" element={<OnboardingGuard><WineCreate /></OnboardingGuard>} />
            <Route path="/wines/:id" element={<OnboardingGuard><WineDetail /></OnboardingGuard>} />
            <Route path="/wines/:id/edit" element={<OnboardingGuard><WineEdit /></OnboardingGuard>} />
            <Route path="/experiences" element={<OnboardingGuard><Experiences /></OnboardingGuard>} />
            <Route path="/itineraries" element={<OnboardingGuard><Itineraries /></OnboardingGuard>} />
            <Route path="/itineraries/new" element={<OnboardingGuard><ItineraryNewPage /></OnboardingGuard>} />
            <Route path="/itineraries/:slug" element={<OnboardingGuard><ItineraryViewPage /></OnboardingGuard>} />
            <Route path="/itineraries/:slug/edit" element={<OnboardingGuard><ItineraryEditPage /></OnboardingGuard>} />
            <Route path="/events" element={<OnboardingGuard><Events /></OnboardingGuard>} />
            <Route path="/events/new" element={<OnboardingGuard><EventCreate /></OnboardingGuard>} />
            <Route path="/events/:id" element={<OnboardingGuard><EventDetail /></OnboardingGuard>} />
            <Route path="/events/:id/edit" element={<OnboardingGuard><EventEdit /></OnboardingGuard>} />
            <Route path="/tasting-experiences/new" element={<OnboardingGuard><TastingExperienceCreate /></OnboardingGuard>} />
            <Route path="/tasting-experiences/:id" element={<OnboardingGuard><TastingExperienceDetail /></OnboardingGuard>} />
            <Route path="/tasting-experiences/:id/edit" element={<OnboardingGuard><TastingExperienceEdit /></OnboardingGuard>} />
          
            {/* Admin Routes - Protected by both OnboardingGuard and ProtectedAdminRoute */}
            <Route
              path="/admin/venues/create"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <VenueCreate />
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/content"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminContent />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/users"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminUsers />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminUserDetail />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminSettings />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/moderation"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminModeration />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminFeedback />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/feedback/:id"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminFeedbackDetail />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/media"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminMediaManagement />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/media-library-test"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <MediaLibraryTest />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
            <Route
              path="/admin/media-test"
              element={
                <OnboardingGuard>
                  <ProtectedAdminRoute>
                    <AdminLayout>
                      <AdminMediaTest />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                </OnboardingGuard>
              }
            />
          
            {/* Test routes - wrapped by OnboardingGuard */}
            <Route path="/test" element={<OnboardingGuard><ApiTest /></OnboardingGuard>} />
            <Route path="/cloudinary-test" element={<OnboardingGuard><CloudinaryTest /></OnboardingGuard>} />
            <Route path="/shared-media-test" element={<OnboardingGuard><SharedMediaTest /></OnboardingGuard>} />
            <Route path="/hero-carousel-demo" element={<OnboardingGuard><HeroCarouselDemo /></OnboardingGuard>} />
            <Route path="/typography-demo" element={<OnboardingGuard><TypographyDemo /></OnboardingGuard>} />
            <Route path="/button-test" element={<OnboardingGuard><ButtonTest /></OnboardingGuard>} />
            <Route path="/design-system-test" element={<OnboardingGuard><DesignSystemTest /></OnboardingGuard>} />
          </Routes>
        </Layout>
      </AgeGate>
    </AuthProvider>
  );
}

export default App;





